import { useState, useEffect, useRef } from 'react';
import { ChevronDown, BookOpen, GraduationCap, Users, Globe, Award } from 'lucide-react';

export default function EducationComponent() {
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, sections.length);
  }, []);

  // Scroll to next section
  const scrollToNext = () => {
    if (activeSection < sections.length - 1) {
      const nextIndex = activeSection + 1;
      setActiveSection(nextIndex);
      sectionRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle scroll events to determine active section
  useEffect(() => {
    const handleScroll = () => {
      const pageTop = window.pageYOffset;
      const pageBottom = pageTop + window.innerHeight;
      
      sectionRefs.current.forEach((ref, index) => {
        if (!ref) return;
        
        const el = ref;
        const rect = el.getBoundingClientRect();
        const elTop = rect.top + pageTop;
        const elBottom = elTop + rect.height;
        
        // If element is in viewport
        if (elTop < pageBottom && elBottom > pageTop) {
          setActiveSection(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const sections = [
    {
      title: "Dutch-India Education Collaboration",
      content: "A strategic partnership to transform education in India through Dutch expertise and innovation. Together, we're building a future where quality education drives economic growth and social development across the region.",
      image: "/images/dutch-india.png",
      imageAlt: "Dutch-India Education Partnership"
    },
    {
      title: "Strengthen Higher Education Collaboration",
      content: "Partner with Dutch universities and research institutions to establish joint degree programs, academic exchange initiatives, and collaborative research centers in areas such as renewable energy, water management, agriculture, and digital technology. These partnerships will elevate research standards and provide students with globally recognized qualifications.",
      image: "/images/strengthen.jpg",
      imageAlt: "University Research Collaboration"
    },
    {
      title: "Enhance Vocational and Skill-Based Training",
      content: "Introduce Dutch best practices in vocational education by co-developing skill development programs aligned with industry needs, particularly in sectors like tourism, manufacturing, and sustainable agriculture. This practical approach ensures graduates are job-ready with skills that match market demands.",
      image: "/images/skills-hero.jpg",
      imageAlt: "Vocational Training Program"
    },
    {
      title: "Establish Centers of Excellence",
      content: "Develop Centers of Excellence in partnership with Dutch institutions focusing on innovation, applied sciences, and technical education, aiming to boost employability and innovation among youth in India. These specialized centers will become innovation hubs where industry and academia collaborate to solve real-world challenges.",
      image: "/images/coe.jpg",
      imageAlt: "Center of Excellence"
    },
    {
      title: "Promote Student and Faculty Exchange Programs",
      content: "Facilitate bi-directional exchange of students and faculty between India and the Netherlands to foster global exposure, intercultural competence, and research collaboration. These exchanges create lasting networks and introduce diverse perspectives that enrich the educational experience for all participants.",
      image: "/images/faculty.jpg",
      imageAlt: "Student Exchange Program"
    },
    {
      title: "Expected Outcomes",
      outcomes: [
        {
          icon: <BookOpen className="w-10 h-10 text-blue-600 mb-4" />,
          title: "Educational Hub",
          content: "Transform India into a regional hub for quality education and research."
        },
        {
          icon: <GraduationCap className="w-10 h-10 text-blue-600 mb-4" />,
          title: "Global Skills",
          content: "Empower youth with globally relevant skills and knowledge."
        },
        {
          icon: <Users className="w-10 h-10 text-blue-600 mb-4" />,
          title: "International Attraction",
          content: "Attract international students and academic investments."
        },
        {
          icon: <Globe className="w-10 h-10 text-blue-600 mb-4" />,
          title: "Academic Partnerships",
          content: "Foster long-term academic and scientific partnerships."
        },
        {
          icon: <Award className="w-10 h-10 text-blue-600 mb-4" />,
          title: "Innovation-Driven Development",
          content: "Support socio-economic development through education-driven innovation."
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {sections.map((section, index) => (
        <div
          key={index}
          ref={el => sectionRefs.current[index] = el}
          className={`min-h-screen flex flex-col justify-center items-center p-8 ${
            index % 2 === 0 ? 'bg-blue-50' : 'bg-white'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">{section.title}</h2>
            
            {/* Regular section with content and image */}
            {!section.outcomes && (
              <>
                <p className="text-xl text-gray-700 mb-12">{section.content}</p>
                <div className="rounded-xl overflow-hidden shadow-xl mb-10">
                  <img 
                    src={section.image} 
                    alt={section.imageAlt} 
                    className="w-full object-cover" 
                  />
                </div>
              </>
            )}
            
            {/* Outcomes section */}
            {section.outcomes && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mt-8">
                {section.outcomes.map((outcome, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
                    {outcome.icon}
                    <h3 className="text-xl font-bold text-blue-800 mb-3">{outcome.title}</h3>
                    <p className="text-gray-700">{outcome.content}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Show scroll down indicator if not the last section */}
            {index < sections.length - 1 && (
              <button 
                onClick={scrollToNext}
                className="mt-10 text-blue-600 hover:text-blue-800 flex flex-col items-center animate-bounce"
              >
                <span className="mb-2">Scroll for more</span>
                <ChevronDown className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}